/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Boot. Configuration is fetched at RUNTIME from /api/config, so one bundle
 * works in dev and production and a client-id change needs no rebuild.
 */

import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import App from "./App.tsx";
import { GuestListView } from "./features/shopping/GuestListView.tsx";
import { AuthBridge } from "./auth/AuthBridge.tsx";
import { PrefsProvider } from "./i18n/index.tsx";
import { fetchConfig, oidcSettings, type RuntimeConfig } from "./auth/oidc.ts";
import { NotConfigured } from "./features/auth/NotConfigured.tsx";
import { Loading, ErrorBox } from "./components/ui.tsx";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, err) => {
        // Retrying a 4xx just repeats the same rejection.
        const status = (err as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return count < 2;
      },
    },
  },
});

function Boot() {
  const [cfg, setCfg] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig().then(setCfg).catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <div className="p-6"><ErrorBox message={error} /></div>;
  if (!cfg) return <div className="grid min-h-screen place-items-center"><Loading /></div>;
  if (!cfg.configured) return <NotConfigured />;

  return (
    <AuthProvider
      {...oidcSettings(cfg)}
      onSigninCallback={() => {
        // Strip ?code=&state= so a refresh doesn't replay the exchange.
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      <QueryClientProvider client={queryClient}>
        <PrefsProvider>
          <AuthBridge>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthBridge>
        </PrefsProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

// A list shared through a temporary link (/s/<token>) opens without signing
// in — the token is the key — and without the app around it.
const guest = /^\/s\/([A-Za-z0-9_-]{20,64})\/?$/.exec(window.location.pathname);

createRoot(document.getElementById("root")!).render(
  <StrictMode>{guest ? <GuestListView token={guest[1]!} /> : <Boot />}</StrictMode>,
);
